import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Industrial.css';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaIndustry, FaLayerGroup, FaRuler } from 'react-icons/fa';

export default function Industrial() {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [cardWidth, setCardWidth] = useState(410);

  // Calculate card width on mount and resize
  useEffect(() => {
    const calculateCardWidth = () => {
      if (window.innerWidth <= 480) {
        setCardWidth(315);
      } else if (window.innerWidth <= 768) {
        setCardWidth(340);
      } else if (window.innerWidth <= 1024) {
        setCardWidth(370);
      } else {
        setCardWidth(410);
      }
    };

    calculateCardWidth();
    window.addEventListener('resize', calculateCardWidth);
    return () => window.removeEventListener('resize', calculateCardWidth);
  }, []);

  // Industrial property data
  const properties = [
    {
      id: 1,
      slug: 'manesar-industrial-warehouse',
      title: 'Industrial Warehouse',
      location: 'Manesar, Gurgaon',
      type: 'Warehouse',
      builder: 'DLF Industrial',
      floors: 'Ground Floor',
      area: '25000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 2,
      slug: 'faridabad-manufacturing-unit',
      title: 'Manufacturing Unit',
      location: 'Faridabad, Haryana',
      type: 'Factory',
      builder: 'Prestige Industrial',
      floors: 'Ground + 1st Floor',
      area: '35000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/1095814/pexels-photo-1095814.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 3,
      slug: 'noida-logistics-park',
      title: 'Logistics Park',
      location: 'Noida, Uttar Pradesh',
      type: 'Logistics',
      builder: 'Oberoi Industrial',
      floors: 'Multiple Units',
      area: '50000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 4,
      slug: 'bhiwadi-industrial-shed',
      title: 'Industrial Shed',
      location: 'Bhiwadi, Rajasthan',
      type: 'Shed',
      builder: 'Tata Industrial',
      floors: 'Ground Floor',
      area: '20000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/1549200/pexels-photo-1549200.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 5,
      slug: 'kundli-cold-storage',
      title: 'Cold Storage Facility',
      location: 'Kundli, Haryana',
      type: 'Cold Storage',
      builder: 'Lodha Industrial',
      floors: 'Ground Floor',
      area: '30000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 6,
      slug: 'ghaziabad-industrial-plot',
      title: 'Industrial Plot',
      location: 'Ghaziabad, UP',
      type: 'Plot',
      builder: 'Godrej Industrial',
      floors: 'Vacant Land',
      area: '40000 sq.ft',
      category: 'Industrial',
      image: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const scroll = (direction) => {
    const container = scrollRef.current;
    
    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <section className="industrial-section">
        <div className="industrial-container">
          
          {/* Header */}
          <div className="section-header">
            <h2>Latest Industrial Properties</h2>
            <p>Explore our premium industrial spaces for your business needs</p>
          </div>

          {/* Properties Slider */}
          <div className="slider-wrapper">
            <button 
              className="slider-btn slider-btn-left" 
              onClick={() => scroll('left')}
              aria-label="Scroll Left"
            >
              <FaChevronLeft />
            </button>

            <div 
              className="properties-slider" 
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {properties.map((property, index) => (
                <Link 
                  to={`/properties/industrial/${property.slug}`}
                  key={property.id}
                  style={{ textDecoration: 'none' }}
                >
                  <div 
                    className="property-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="property-image">
                      <img src={property.image} alt={property.title} />
                      <div className="property-badge">{property.category}</div>
                    </div>

                    <div className="property-content">
                      <h3>{property.title}</h3>
                      
                      <div className="property-details">
                        <div className="detail-item">
                          <FaMapMarkerAlt className="detail-icon" />
                          <span>{property.location}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaIndustry className="detail-icon" />
                          <span>{property.type}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaRuler className="detail-icon" />
                          <span>{property.area}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaLayerGroup className="detail-icon" />
                          <span>{property.floors}</span>
                        </div>
                      </div>

                      <div className="property-footer">
                        <div className="price-tag">Price on Request</div>
                        <button className="contact-btn">View Details</button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <button 
              className="slider-btn slider-btn-right" 
              onClick={() => scroll('right')}
              aria-label="Scroll Right"
            >
              <FaChevronRight />
            </button>
          </div>

          {/* View All Button */}
          <div className="view-all-wrapper">
            <Link to="/contact">
              <button className="view-all-btn">Contact Us</button>
            </Link>
          </div>

        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="contact-cta-section">
        <div className="cta-content">
          <h2>Need Industrial Space for Your Business?</h2>
          <p>Connect with our industrial property experts today</p>
          <Link to="/contact" className="cta-link-btn">
            Get In Touch
          </Link>
        </div>
      </section>
    </>
  );
}
